import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { canonicalForRoute } from './canonical'

const ORIGIN = import.meta.env.VITE_SITE_ORIGIN ?? 'https://www.takefav.com'
const DEFAULT_TITLE = 'TakeFav — Smart Shopping & Favorite Finds'
const TITLE_SUFFIX = ' | TakeFav'
const DEFAULT_DESC =
  'Discover trending products, smart guides, and curated picks to save, compare, and shop better.'
const DEFAULT_IMAGE = new URL('/images/share/og-takefav.webp', ORIGIN).toString()
const SITE_NAME = 'TakeFav'
const TWITTER_HANDLE = '@takefav' // or leave blank if none

// keep <60 chars title, <160 chars description
function clip(s: string, max: number) {
  return s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s
}

/**
 * Append " | TakeFav" unless the caller already branded the title.
 *
 * Several views pass pre-branded titles (e.g. 'Products | TakeFav'), which used
 * to come out as 'Products | TakeFav | TakeFav'. Matches ' | TakeFav' and the
 * ' - TakeFav' / 'on TakeFav' variants other callers use.
 */
function withSuffix(title?: string) {
  if (!title) return DEFAULT_TITLE
  return /[|\-–—]\s*TakeFav\s*$|\bTakeFav\s*$/i.test(title.trim())
    ? title.trim()
    : title.trim() + TITLE_SUFFIX
}

// Build full meta set
export function buildSeoMeta(opts: {
  route: RouteLocationNormalizedLoaded
  title?: string
  description?: string
  keywords?: string[] | string
  image?: string
  robots?: string           // e.g. 'noindex,nofollow' for staging
  type?: 'website' | 'article' | 'product' // Open Graph type
  lang?: string             // e.g. 'en', 'he'
  canonical?: string        // Canonical URL override
  structuredData?: any      // JSON-LD structured data
}) {
  const {
    route,
    type = 'website',
    lang = 'en',
  } = opts

  const title = clip(withSuffix(opts.title), 60)
  const description = clip(opts.description || DEFAULT_DESC, 160)
  const image = opts.image || DEFAULT_IMAGE
  const keywords = Array.isArray(opts.keywords) ? opts.keywords.join(', ') : (opts.keywords || '')

  // Canonical URL - prioritize override, then route canonical, then default
  const canonical = opts.canonical || canonicalForRoute(route) || `https://www.takefav.com${route.path}`

  return {
    htmlAttrs: { lang },
    title,
    meta: [
      { name: 'description', content: description },
      // FYI: Google ignores "keywords" nowadays, but harmless if you still want it:
      ...(keywords ? [{ name: 'keywords', content: keywords }] : []),
      ...(opts.robots ? [{ name: 'robots', content: opts.robots }] : []),

      // Open Graph
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: image },

      // Twitter Cards
      { name: 'twitter:card', content: 'summary_large_image' },
      ...(TWITTER_HANDLE ? [{ name: 'twitter:site', content: TWITTER_HANDLE }] : []),
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ],
    // Always include canonical link for SEO
    link: [{ rel: 'canonical', href: canonical }],
    // Include structured data if provided
    ...(opts.structuredData ? { script: [{ type: 'application/ld+json', children: JSON.stringify(opts.structuredData) }] } : {}),
  }
}
